import { Component } from '@theme/component';

/**
 * A custom element that manages deferred payment consent functionality.
 * Integrates with the existing validation system and provides consent checking
 * for add-to-cart operations.
 */
class DeferredPaymentConsentComponent extends Component {
  requiredRefs = ['consentForm', 'consentCheckbox'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {boolean} */
  #consentGiven = false;

  /** @type {boolean} */
  #showOnlyForInsurance = true;

  /** @type {boolean} */
  #disableAddToCart = true;

  /** @type {string} */
  #errorMessage = 'You must agree to the terms and conditions to proceed.';

  /** @type {boolean} */
  #insuranceSkipped = false;

  connectedCallback() {
    super.connectedCallback();

    // Initialize configuration from data attributes
    this.#initializeConfiguration();

    // Set up event listeners
    this.#setupEventListeners();
    
    // Initialize state
    this.#initializeState();
    
    // Inject hidden input into product form for line item properties
    this.#injectHiddenInputsIntoProductForm();
    
    // Set up global functions for compatibility
    this.#setupGlobalFunctions();
    
    // Check for existing insurance skip state
    this.#checkInsuranceSkipState();
    
    // Integrate with add-to-cart validation system
    this.#integrateWithValidationSystem();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Initialize configuration from data attributes
   */
  #initializeConfiguration() {
    this.#showOnlyForInsurance = this.dataset.showOnlyForInsurance !== 'false';
    this.#disableAddToCart = this.dataset.disableAddToCart !== 'false';
    this.#errorMessage = this.dataset.errorMessage || this.#errorMessage;
  }

  /**
   * Set up event listeners
   */
  #setupEventListeners() {
    const { signal } = this.#abortController;

    // Consent checkbox change
    this.refs.consentCheckbox.addEventListener('change', this.#handleConsentChange.bind(this), { signal });

    // Listen for insurance skip events
    document.addEventListener('insuranceSkipped', this.#handleInsuranceSkipped.bind(this), { signal });

    // Listen for warehouse state changes
    document.addEventListener('warehouseStateChanged', this.#handleWarehouseStateChanged.bind(this), { signal });

    // Listen for measurement state changes to update validation
    document.addEventListener('measurementStateChanged', this.#updateValidationState.bind(this), { signal });

    // Set up custom validity messages
    this.refs.consentCheckbox.addEventListener('invalid', this.#handleInvalidConsent.bind(this), { signal });
    this.refs.consentCheckbox.addEventListener('input', this.#clearCustomValidity.bind(this), { signal });
  }

  /**
   * Initialize component state
   */
  #initializeState() {
    // Set initial consent state
    this.#consentGiven = this.refs.consentCheckbox.checked;
    
    // Update hidden field if consent is already given
    if (this.#consentGiven) {
      this.#updateHiddenField();
    }
  }

  /**
   * Inject hidden input into the product form for line item properties
   * Following the same pattern as the insurance component
   */
  #injectHiddenInputsIntoProductForm() {
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;

    const properties = ['_deferredPaymentConsent'];
    
    properties.forEach(property => {
      if (!productForm.querySelector(`input[name="properties[${property}]"]`)) {
        const input = Object.assign(document.createElement('input'), {
          type: 'hidden',
          name: `properties[${property}]`,
          value: ''
        });
        productForm.appendChild(input);
      }
    });
  }

  /**
   * Update a form-injected hidden input value
   * @param {string} propertyName - The property name
   * @param {string} value - The value to set
   */
  #updateFormInjectedInput(propertyName, value) {
    const input = document.querySelector(`form[action*="/cart/add"] input[name="properties[${propertyName}]"]`);
    if (input) input.value = value;
  }

  /**
   * Handle consent checkbox change
   * @param {Event} event - The change event
   */
  #handleConsentChange(event) {
    this.#consentGiven = event.target.checked;
    
    if (this.#consentGiven) {
      // Store current timestamp when consent is given
      this.#updateHiddenField();
      this.#clearValidationMessage();
    } else {
      // Clear timestamp when consent is revoked
      this.#updateFormInjectedInput('_deferredPaymentConsent', '');
    }
    
    // Update validation state
    this.#updateValidationState();
    
    // Dispatch custom event for other components
    document.dispatchEvent(new CustomEvent('consentChanged', {
      detail: { 
        consentGiven: this.#consentGiven,
        timestamp: this.#getConsentTimestamp()
      }
    }));
  }

  /**
   * Update hidden field with current timestamp
   */
  #updateHiddenField() {
    const currentDateTime = new Date().toISOString();
    this.#updateFormInjectedInput('_deferredPaymentConsent', currentDateTime);
  }

  /**
   * Get the current consent timestamp
   * @returns {string} The current consent timestamp
   */
  #getConsentTimestamp() {
    const input = document.querySelector(`form[action*="/cart/add"] input[name="properties[_deferredPaymentConsent]"]`);
    return input ? input.value : '';
  }

  /**
   * Handle insurance skip state change
   * @param {CustomEvent} event - The insurance skipped event
   */
  #handleInsuranceSkipped(event) {
    this.#insuranceSkipped = event.detail.skipped;
    
    if (this.#showOnlyForInsurance) {
      if (this.#insuranceSkipped) {
        // Check if warehouse validation is active
        const isWarehouseActive = this.#isWarehouseValidationActive();
        
        if (isWarehouseActive) {
          // Keep consent form visible when warehouse validation is active
          this.refs.consentForm.classList.remove('hidden');
          this.refs.consentCheckbox.required = true;
        } else {
          // Hide consent form only when insurance is truly skipped without warehouse
          this.refs.consentForm.classList.add('hidden');
          this.refs.consentCheckbox.required = false;
        }
      } else {
        // Show consent form when insurance is active
        this.refs.consentForm.classList.remove('hidden');
        this.refs.consentCheckbox.required = true;
      }
    }
    
    // Update validation state
    this.#updateValidationState();
  }

  /**
   * Handle warehouse state changes
   * @param {CustomEvent} event - The warehouse state changed event
   */
  #handleWarehouseStateChanged(event) {
    // Re-evaluate consent form visibility when warehouse state changes
    if (this.#showOnlyForInsurance && this.#insuranceSkipped) {
      const isWarehouseActive = this.#isWarehouseValidationActive();
      
      if (isWarehouseActive) {
        // Keep consent form visible when warehouse validation is active
        this.refs.consentForm.classList.remove('hidden');
        this.refs.consentCheckbox.required = true;
      } else {
        // Hide consent form only when insurance is truly skipped without warehouse
        this.refs.consentForm.classList.add('hidden');
        this.refs.consentCheckbox.required = false;
      }
    }
    
    // Update validation state
    this.#updateValidationState();
  }

  /**
   * Check if warehouse validation is currently active
   * @returns {boolean} Whether warehouse validation is active
   */
  #isWarehouseValidationActive() {
    // Check if availity component exists and has warehouse validation enabled
    const availityComponent = document.querySelector('availity-insurance-warehouse-checker-component');
    if (!availityComponent) return false;
    
    // Check if warehouse section is visible (this indicates warehouse mode)
    const warehouseSection = availityComponent.querySelector('[data-ref="warehouseSection"]');
    if (warehouseSection && warehouseSection.style.display !== 'none' && !warehouseSection.classList.contains('hidden')) {
      return true;
    }
    
    // Check if insurance was skipped (which usually means warehouse mode is active)
    if (this.#insuranceSkipped) {
      return true;
    }
    
    // Check session storage for warehouse mode
    const warehouseMode = sessionStorage.getItem('warehouseMode');
    if (warehouseMode === 'true') {
      return true;
    }
    
    return false;
  }

  /**
   * Check for existing insurance skip state from session storage
   */
  #checkInsuranceSkipState() {
    const storedInsuranceSkipped = sessionStorage.getItem('insuranceSkipped');
    if (storedInsuranceSkipped === 'true') {
      this.#insuranceSkipped = true;
      
      if (this.#showOnlyForInsurance) {
        // Check if warehouse validation is active before hiding
        const isWarehouseActive = this.#isWarehouseValidationActive();
        
        if (isWarehouseActive) {
          // Keep consent form visible when warehouse validation is active
          this.refs.consentForm.classList.remove('hidden');
          this.refs.consentCheckbox.required = true;
        } else {
          // Hide consent form only when insurance is truly skipped without warehouse
          this.refs.consentForm.classList.add('hidden');
          this.refs.consentCheckbox.required = false;
        }
      }
    }
  }

  /**
   * Integrate with existing add-to-cart validation system
   */
  #integrateWithValidationSystem() {
    if (!this.#disableAddToCart) return;
    
    // Find and enhance existing validation components
    this.#enhanceExistingValidation();
  }

  /**
   * Enhance existing validation components to include consent checking
   */
  #enhanceExistingValidation() {
    // Look for availity insurance warehouse checker component
    const availityComponent = document.querySelector('availity-insurance-warehouse-checker-component');
    if (availityComponent) {
      // Store reference to original validation method
      const originalValidateInsuranceFields = availityComponent.validateInsuranceFields;
      
      // Enhance with consent validation
      availityComponent.validateInsuranceFields = () => {
        // Run original validation first
        const originalResult = originalValidateInsuranceFields ? originalValidateInsuranceFields.call(availityComponent) : true;
        
        // Add consent validation
        const consentValid = this.#validateConsent();
        
        return originalResult && consentValid;
      };
    }
    
    // Also integrate directly with product form submissions
    this.#interceptProductFormSubmissions();
  }

  /**
   * Intercept product form submissions to add consent validation
   */
  #interceptProductFormSubmissions() {
    // Wait for existing product form interception to be set up
    setTimeout(() => {
      const productForms = document.querySelectorAll('product-form-component');
      
      productForms.forEach(productForm => {
        if (productForm.deferredConsentIntercepted) return;
        productForm.deferredConsentIntercepted = true;
        
        // Store the existing handleSubmit method (which may already be enhanced by availity)
        const existingHandleSubmit = productForm.handleSubmit.bind(productForm);
        
        // Override with our consent validation wrapper
        productForm.handleSubmit = (event) => {
          // Check if add-to-cart validation is enabled
          if (!this.#disableAddToCart) {
            return existingHandleSubmit(event);
          }
          
          // Only validate consent if form is visible (not hidden due to insurance skip)
          if (!this.refs.consentForm.classList.contains('hidden')) {
            if (!this.#validateConsent()) {
              event.preventDefault();
              return;
            }
          }
          
          return existingHandleSubmit(event);
        };
      });
    }, 100);
  }

  /**
   * Validate consent checkbox
   * @returns {boolean} Whether consent is valid
   */
  #validateConsent() {
    // If form is hidden (insurance skipped), consent is not required
    if (this.refs.consentForm.classList.contains('hidden')) {
      return true;
    }
    
    // Check if consent is given
    if (!this.#consentGiven) {
      this.#showValidationMessage();
      this.refs.consentCheckbox.focus();
      return false;
    }
    
    this.#clearValidationMessage();
    return true;
  }

  /**
   * Show validation error message
   */
  #showValidationMessage() {
    if (this.refs.notificationArea) {
      this.refs.notificationArea.textContent = this.#errorMessage;
      this.refs.notificationArea.style.display = 'block';
    }
  }

  /**
   * Clear validation error message
   */
  #clearValidationMessage() {
    if (this.refs.notificationArea) {
      this.refs.notificationArea.style.display = 'none';
      this.refs.notificationArea.textContent = '';
    }
  }

  /**
   * Handle invalid consent checkbox
   * @param {Event} event - The invalid event
   */
  #handleInvalidConsent(event) {
    event.target.setCustomValidity(this.#errorMessage);
  }

  /**
   * Clear custom validity message
   * @param {Event} event - The input event
   */
  #clearCustomValidity(event) {
    event.target.setCustomValidity('');
  }

  /**
   * Update validation state when other components change
   */
  #updateValidationState() {
    // Trigger validation update in other components if they exist
    const availityComponent = document.querySelector('availity-insurance-warehouse-checker-component');
    if (availityComponent && typeof availityComponent.updateAddToCartButtonState === 'function') {
      availityComponent.updateAddToCartButtonState();
    }
  }

  /**
   * Set up global functions for compatibility
   */
  #setupGlobalFunctions() {
    // Make consent checking available globally for other components
    window.isDeferredPaymentConsentGiven = () => {
      // If form is hidden, consent is not required
      if (this.refs.consentForm.classList.contains('hidden')) {
        return true;
      }
      return this.#consentGiven;
    };
    
    window.validateDeferredPaymentConsent = () => {
      return this.#validateConsent();
    };
  }
}

// Register the custom element
if (!customElements.get('deferred-payment-consent-component')) {
  customElements.define('deferred-payment-consent-component', DeferredPaymentConsentComponent);
}